import {
    EnterprisePricingTier,
    EnterprisePricingTierAccumulatedPrice,
    EnterprisePriceCalculations
} from "./typings";

export enum EnterprisePriceCalculationsError {
    INVALID_TIER = "INVALID_TIER",
    MIN_USERS_ERROR = "MIN_USERS_ERROR",
    MAX_USERS_ERROR = "MAX_USERS_ERROR"
}

export function accumulatedEnterpriseTiers(
    tiers: readonly EnterprisePricingTier[]
) {
    const sortedTiers = [...tiers].sort((a, b) => a.above - b.above);
    const accumulatedTiers = sortedTiers.reduce<
        EnterprisePricingTierAccumulatedPrice[]
    >((accumulated, tier, i) => {
        if (i === 0) {
            accumulated[i] = {
                ...tier,
                prevTierAccumulatedPrice: 0
            };
        } else {
            const prev = accumulated[i - 1];
            if (prev) {
                accumulated[i] = {
                    ...tier,
                    prevTierAccumulatedPrice:
                        (tier.above - prev.above) * prev.at +
                        prev.prevTierAccumulatedPrice
                };
            }
        }

        return accumulated;
    }, []);

    return accumulatedTiers.sort((a, b) => b.above - a.above);
}

export class EnterprisePricingCalculator {
    private readonly tiers: readonly EnterprisePricingTierAccumulatedPrice[];
    private readonly minUsers: number;
    private readonly maxUsers: number;
    private readonly defaultCalculations: EnterprisePriceCalculations = {
        annualPrice: "0",
        annualPricePerUser: "0.00",
        monthlyPricePerUser: "0.00"
    };

    public constructor(
        tiers: EnterprisePricingTier[],
        minUsers = 1,
        maxUsers = 10000000
    ) {
        this.tiers = accumulatedEnterpriseTiers(tiers);
        this.minUsers = minUsers;
        this.maxUsers = maxUsers;
    }

    public getPricesByUserCount(
        userCount: number
    ): [
        EnterprisePriceCalculationsError | undefined,
        EnterprisePriceCalculations
    ] {
        const tier = this.tiers.find((tier) => userCount > tier.above);

        if (userCount < this.minUsers) {
            return [
                EnterprisePriceCalculationsError.MIN_USERS_ERROR,
                this.defaultCalculations
            ];
        }

        if (userCount > this.maxUsers) {
            return [
                EnterprisePriceCalculationsError.MAX_USERS_ERROR,
                this.defaultCalculations
            ];
        }

        if (!tier) {
            return [
                EnterprisePriceCalculationsError.INVALID_TIER,
                this.defaultCalculations
            ];
        }

        const annualPrice =
            (userCount - tier.above) * tier.at + tier.prevTierAccumulatedPrice;
        const annualPricePerUser = annualPrice / userCount;
        const monthlyPricePerUser = annualPricePerUser / 12;

        return [
            undefined,
            {
                annualPrice: annualPrice.toLocaleString("en", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }),
                annualPricePerUser: annualPricePerUser.toLocaleString("en", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }),
                monthlyPricePerUser: monthlyPricePerUser.toLocaleString("en", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })
            }
        ];
    }
}
